import { matchesOption, displayOptionInput } from "../../../i18n/options";
import { useLanguage, t } from "../../../i18n";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Row, Col, Form, Button, Spinner } from "react-bootstrap";
import "./ProfileForm.css";
import getCities from "../../../features/profile/services/getCities";
import getNeighbourhoods from "../../../features/profile/services/getNeighbourhoods";
import getNationalities from "../../../features/profile/services/getNationalities";
import getPropertyTypes from "../../../features/profile/services/getPropertyTypes";
const TEXT_ONLY_REGEX = /^[\p{L}\p{M}\s]+$/u;
const sanitizeTextOnly = (value, maxLength = 20) => {
  return value.replace(/[^\p{L}\p{M}\s]/gu, "").replace(/\s+/g, " ").replace(/^\s+/, "").slice(0, maxLength);
};
const sanitizePhoneNumber = value => {
  return value.replace(/\D/g, "").slice(0, 10);
};
const normalizeOption = option => {
  if (typeof option === "object" && option !== null) {
    return {
      value: String(option.value ?? option.id ?? ""),
      label: String(option.label ?? option.name ?? ""),
      raw: option
    };
  }
  return {
    value: String(option),
    label: String(option),
    raw: option
  };
};
const ROOM_TYPE_OPTIONS = [{
  value: "bedspace",
  label: "Bedspace"
}, {
  value: "shared room",
  label: "Shared Room"
}, {
  value: "private room",
  label: "Private Room"
}, {
  value: "studio room",
  label: "Studio Room"
}, {
  value: "partition room",
  label: "Partition Room"
}];
const HOUSE_TYPE_VALUE_LABEL_MAP = {
  "apartment": "Apartment",
  "villa": "Villa",
  "studio apartment": "Studio Apartment",
  "compound": "Compound",
  "standalone house": "Standalone House",
  "labour camp": "Labour Camp"
};
const HOUSE_TYPE_ALIASES = {
  apartment: "apartment",
  apartments: "apartment",
  villa: "villa",
  villas: "villa",
  "studio apartment": "studio apartment",
  studio_apartment: "studio apartment",
  "studio apartments": "studio apartment",
  compound: "compound",
  compounds: "compound",
  "standalone house": "standalone house",
  standalone_house: "standalone house",
  "stand alone house": "standalone house",
  "stand-alone house": "standalone house",
  "labour camp": "labour camp",
  labour_camp: "labour camp",
  "labor camp": "labour camp"
};
const extractPrimitiveValue = item => {
  if (item === undefined || item === null) return "";
  if (typeof item === "object") {
    return String(item.value ?? item.id ?? item.city_id ?? item.neighbourhood_id ?? item.property_type_id ?? "");
  }
  return String(item);
};
const normalizeIncomingMultiValue = value => {
  if (Array.isArray(value)) {
    return value.map(item => extractPrimitiveValue(item)).filter(Boolean);
  }
  if (value === undefined || value === null || value === "") {
    return [];
  }
  const singleValue = extractPrimitiveValue(value);
  return singleValue ? [singleValue] : [];
};
const buildInitialLabelMapFromItems = (items = []) => {
  if (!Array.isArray(items)) return {};
  return items.reduce((acc, item) => {
    if (typeof item === "object" && item !== null) {
      const value = extractPrimitiveValue(item);
      const label = String(item.label ?? item.name ?? item.title ?? "").trim();
      if (value && label) {
        acc[value] = label;
      }
    }
    return acc;
  }, {});
};
const normalizeFreeTextKey = value => {
  return String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
};
const normalizeRoomTypeValueForForm = item => {
  const raw = typeof item === "object" && item !== null ? item.value ?? item.label ?? item.name ?? item.id ?? "" : item;
  const normalized = normalizeFreeTextKey(raw);
  const matched = ROOM_TYPE_OPTIONS.find(option => {
    const optionNormalizedValue = normalizeFreeTextKey(option.value);
    const optionNormalizedLabel = normalizeFreeTextKey(option.label);
    return normalized === optionNormalizedValue || normalized === optionNormalizedLabel;
  });
  return matched ? matched.value : "";
};
const normalizeRoomTypeMultiValueForForm = value => {
  const input = Array.isArray(value) ? value : value !== undefined && value !== null && value !== "" ? [value] : [];
  return input.map(item => normalizeRoomTypeValueForForm(item)).filter(Boolean);
};
const normalizeHouseTypeValueForForm = item => {
  const raw = typeof item === "object" && item !== null ? item.value ?? item.label ?? item.name ?? item.title ?? item.id ?? "" : item;
  const normalized = normalizeFreeTextKey(raw);
  if (HOUSE_TYPE_ALIASES[normalized]) {
    return HOUSE_TYPE_ALIASES[normalized];
  }
  const underscored = normalized.replace(/\s+/g, "_");
  if (HOUSE_TYPE_VALUE_LABEL_MAP[underscored]) {
    return underscored;
  }
  return "";
};
const normalizeHouseTypeMultiValueForForm = value => {
  const input = Array.isArray(value) ? value : value !== undefined && value !== null && value !== "" ? [value] : [];
  return input.map(item => normalizeHouseTypeValueForForm(item)).filter(Boolean);
};
const normalizeGenderPreferenceValueForForm = value => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male" || normalized === "male only") return "Male Only";
  if (normalized === "female" || normalized === "female only") return "Female Only";
  return value ? String(value) : "";
};
const getSafeLabelText = (label, fallback = "options") => {
  if (typeof label === "string") {
    return label;
  }
  if (React.isValidElement(label)) {
    const children = label.props?.children;
    if (typeof children === "string") {
      return children;
    }
    if (Array.isArray(children)) {
      const textParts = children.map(child => {
        if (typeof child === "string") return child;
        if (typeof child === "number") return String(child);
        return "";
      }).filter(Boolean);
      if (textParts.length > 0) {
        return textParts.join(" ").replace(/\s+/g, " ").trim();
      }
    }
  }
  return fallback;
};
const getArraySignature = arr => {
  if (!Array.isArray(arr)) return "";
  return arr.map(item => String(item)).join("|");
};
const buildUntouchedState = (fields = []) => fields.reduce((acc, field) => {
  acc[field] = false;
  return acc;
}, {});
const buildPreferredNeighbourhoodMeta = (selectedCityIds = [], cacheMap = {}, cityOptions = []) => {
  const cityLabelMap = cityOptions.reduce((acc, city) => {
    const normalized = normalizeOption(city);
    acc[normalized.value] = normalized.label;
    return acc;
  }, {});
  const groupedSections = selectedCityIds.map(cityId => {
    const normalizedCityId = String(cityId);
    const cityLabel = cityLabelMap[normalizedCityId] || "Selected City";
    const cachedItems = Array.isArray(cacheMap[normalizedCityId]) ? cacheMap[normalizedCityId] : [];
    const normalizedOptions = cachedItems.map(item => ({
      value: String(item.id),
      label: item.name,
      id: item.id,
      name: item.name,
      name_ar: item.name_ar || "",
      city_id: item.city_id
    }));
    return {
      cityId: normalizedCityId,
      title: `${cityLabel} Neighbourhoods`,
      options: normalizedOptions
    };
  }).filter(section => section.options.length > 0);
  const flatOptions = groupedSections.flatMap(section => section.options);
  const labelMap = flatOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});
  return {
    groupedSections,
    flatOptions,
    labelMap
  };
};
const normalizeLowercaseValue = value => {
  if (!value && value !== 0) return "";
  return String(value).trim().toLowerCase();
};
const normalizeGenderValueForApi = value => {
  const normalized = normalizeLowercaseValue(value);
  if (normalized === "male" || normalized === "male only") return "male";
  if (normalized === "female" || normalized === "female only") return "female";
  return normalized;
};
const normalizeGenderValueForForm = value => {
  const normalized = normalizeLowercaseValue(value);
  if (normalized === "male" || normalized === "male only") return "Male";
  if (normalized === "female" || normalized === "female only") return "Female";
  return value ? String(value) : "";
};
const normalizeYesNoValueForApi = value => {
  const normalized = normalizeLowercaseValue(value);
  if (normalized === "yes") return "yes";
  if (normalized === "no") return "no";
  return normalized;
};
const normalizeRoomTypeValuesForApi = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items.map(item => normalizeRoomTypeValueForForm(item)).filter(Boolean);
};
const normalizeHouseTypeValuesForApi = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items.map(item => normalizeHouseTypeValueForForm(item)).filter(Boolean);
};
export const profileInitialValues = {
  name: "",
  email: "",
  contactNumber: "",
  whatsappNumber: "",
  age: "",
  gender: "",
  occupation: "",
  nationality: "",
  city: "",
  district: "",
  preferredCity: [],
  preferredNeighbourhood: [],
  budgetMin: "100",
  budgetMax: "50000",
  furnishedPreference: "",
  roomTypePreference: [],
  houseTypePreference: [],
  genderPreference: "",
  aboutMe: ""
};
const genderOptions = ["Male", "Female"];
const furnishedOptions = ["Yes", "No"];
const roomTypeOptions = ROOM_TYPE_OPTIONS;
const genderPreferenceOptions = [{
  value: "Male Only",
  label: "Male Only"
}, {
  value: "Female Only",
  label: "Female Only"
}];
const stepOneFields = ["name", "email", "contactNumber", "whatsappNumber", "age", "gender", "occupation", "nationality", "city", "district"];
const stepTwoFields = ["preferredCity", "preferredNeighbourhood", "budgetMin", "budgetMax", "furnishedPreference", "roomTypePreference", "houseTypePreference", "genderPreference", "aboutMe"];
const normalizeMultiSelectValue = (selectedValues, allOptions) => {
  const normalizedOptions = allOptions.map(normalizeOption);
  const allValues = normalizedOptions.map(item => item.value);
  const hasAllOption = allValues.includes("All");
  const valuesWithoutAll = allValues.filter(item => item !== "All");
  if (hasAllOption && selectedValues.includes("All")) {
    return ["All"];
  }
  return selectedValues.filter(item => hasAllOption ? valuesWithoutAll.includes(item) : allValues.includes(item));
};
function MultiSelectTagField({
  label,
  name,
  options,
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  helperText,
  onSelectionChange,
  disabled = false,
  loading = false,
  searchable = false,
  searchPlaceholder,
  showError,
  onInteract,
  optionLabelMap = {},
  groupedSections = []
}) {
  const {
    language
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const safeLabelText = useMemo(() => getSafeLabelText(label, "options"), [label]);
  const normalizedOptions = useMemo(() => Array.isArray(options) ? options.map(normalizeOption) : [], [options]);
  const normalizedGroupedSections = useMemo(() => {
    if (!Array.isArray(groupedSections)) return [];
    return groupedSections.map(section => ({
      ...section,
      options: Array.isArray(section.options) ? section.options.map(normalizeOption) : []
    }));
  }, [groupedSections]);
  const hasGroupedSections = normalizedGroupedSections.length > 0;
  const selectedValues = Array.isArray(values[name]) ? values[name].map(item => String(item)) : [];
  const flatGroupedOptions = useMemo(() => normalizedGroupedSections.flatMap(section => section.options), [normalizedGroupedSections]);
  const hasAllOption = !hasGroupedSections && normalizedOptions.some(item => item.value === "All");
  const selectedWithoutAll = useMemo(() => selectedValues.filter(item => item !== "All"), [selectedValues]);
  const actualOptions = useMemo(() => {
    if (hasGroupedSections) return flatGroupedOptions;
    return normalizedOptions.filter(item => item.value !== "All");
  }, [flatGroupedOptions, hasGroupedSections, normalizedOptions]);
  const filteredActualOptions = useMemo(() => {
    if (!searchable) return actualOptions;
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return actualOptions;
    return actualOptions.filter(item => matchesOption(item, keyword, language));
  }, [actualOptions, searchable, searchKeyword, language]);
  const filteredGroupedSections = useMemo(() => {
    if (!hasGroupedSections) return [];
    const keyword = searchKeyword.trim().toLowerCase();
    if (!searchable || !keyword) {
      return normalizedGroupedSections;
    }
    return normalizedGroupedSections.map(section => ({
      ...section,
      options: section.options.filter(item => matchesOption(item, keyword, language))
    })).filter(section => section.options.length > 0);
  }, [hasGroupedSections, normalizedGroupedSections, searchable, searchKeyword, language]);
  const selectedLabelMap = useMemo(() => {
    const map = {
      ...optionLabelMap
    };
    normalizedOptions.forEach(item => {
      map[item.value] = item.label;
    });
    flatGroupedOptions.forEach(item => {
      map[item.value] = item.label;
    });
    return map;
  }, [normalizedOptions, flatGroupedOptions, optionLabelMap]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
        }
        if (hasInteracted) {
          setFieldTouched(name, true, true);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hasInteracted, isOpen, name, setFieldTouched]);
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);
  const isSelected = optionValue => selectedValues.includes(String(optionValue));
  const registerInteraction = () => {
    setHasInteracted(true);
    if (onInteract) onInteract(name);
  };
  const updateValues = updatedValues => {
    const normalizedValues = normalizeMultiSelectValue(updatedValues, actualOptions);
    setFieldValue(name, normalizedValues);
    registerInteraction();
    setFieldTouched(name, true, true);
    if (onSelectionChange) {
      onSelectionChange(normalizedValues);
    }
  };
  const handleToggleOption = optionValue => {
    if (disabled) return;
    let updatedValues = [...selectedValues];
    const stringValue = String(optionValue);
    registerInteraction();
    if (!hasGroupedSections && stringValue === "All" && hasAllOption) {
      updatedValues = ["All"];
    } else {
      updatedValues = updatedValues.filter(item => item !== "All");
      if (updatedValues.includes(stringValue)) {
        updatedValues = updatedValues.filter(item => item !== stringValue);
      } else {
        updatedValues.push(stringValue);
      }
    }
    updateValues(updatedValues);
  };
  const handleRemoveTag = (optionValueToRemove, event) => {
    event.stopPropagation();
    if (disabled) return;
    registerInteraction();
    const updatedValues = selectedValues.filter(item => item !== String(optionValueToRemove));
    updateValues(updatedValues);
  };
  const handleClearAll = event => {
    event.stopPropagation();
    if (disabled) return;
    registerInteraction();
    updateValues([]);
  };
  const hasVisibleOptions = hasGroupedSections ? filteredGroupedSections.length > 0 : filteredActualOptions.length > 0;
  return <Form.Group>
      <Form.Label>{t(label)}</Form.Label>

      <div ref={wrapperRef} className={`bb-tag-multiselect ${showError ? "is-invalid" : ""} ${disabled ? "disabled" : ""}`}>

        <div className="bb-tag-multiselect__control" onClick={() => {
        if (disabled) return;
        setIsOpen(prev => !prev);
        registerInteraction();
      }} role="button" tabIndex={disabled ? -1 : 0} onKeyDown={e => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsOpen(prev => !prev);
          registerInteraction();
        }
      }}>

          <div className="bb-tag-multiselect__value">
            {loading ? <span className="bb-tag-multiselect__placeholder">{t("Loading ")}
              {t(safeLabelText)}...
              </span> : selectedValues.length === 0 ? <span className="bb-tag-multiselect__placeholder">{t("Select ")}
              {t(safeLabelText)}
              </span> : hasAllOption && selectedValues.includes("All") ? <div className="bb-tag-multiselect__tags">
                <span className="bb-tag-multiselect__tag bb-tag-multiselect__tag--all">{t("All")}

                <button type="button" className="bb-tag-multiselect__tag-remove" onClick={e => handleRemoveTag("All", e)}>

                    ×
                  </button>
                </span>
              </div> : <div className="bb-tag-multiselect__tags">
                {(hasAllOption ? selectedWithoutAll : selectedValues).map(item => <span key={item} className="bb-tag-multiselect__tag">
                    {t(selectedLabelMap[item] || item)}
                    <button type="button" className="bb-tag-multiselect__tag-remove" onClick={e => handleRemoveTag(item, e)}>

                      ×
                    </button>
                  </span>)}
              </div>}
          </div>

          <div className="bb-tag-multiselect__actions">
            {selectedValues.length > 0 && !disabled && <button type="button" className="bb-tag-multiselect__clear" onClick={handleClearAll}>{t("Clear")}


            </button>}
            <span className={`bb-tag-multiselect__arrow ${isOpen ? "open" : ""}`}>
              ▾
            </span>
          </div>
        </div>

        {isOpen && !disabled && <div className="bb-tag-multiselect__menu">
            {searchable ? <div className="bb-tag-multiselect__search-wrap">
                <Form.Control ref={searchInputRef} type="text" value={searchKeyword} placeholder={t(searchPlaceholder || `Search ${safeLabelText}`)} onChange={e => {
            setSearchKeyword(e.target.value);
            registerInteraction();
          }} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} />

              </div> : null}

            {!hasGroupedSections && hasAllOption && <button type="button" className={`bb-tag-multiselect__option ${isSelected("All") ? "active" : ""}`} onClick={() => handleToggleOption("All")}>

                <span className="bb-tag-multiselect__checkbox">
                  {t(isSelected("All") ? "✓" : "")}
                </span>
                <span>{t("All")}</span>
              </button>}

            {hasVisibleOptions ? hasGroupedSections ? filteredGroupedSections.map(section => <div key={section.cityId || section.title}>
                    <div className="bb-tag-multiselect__group-title">
                      {t(section.title)}
                    </div>

                    {section.options.map(option => <button key={option.value} type="button" className={`bb-tag-multiselect__option ${isSelected(option.value) ? "active" : ""}`} onClick={() => handleToggleOption(option.value)}>

                        <span className="bb-tag-multiselect__checkbox">
                          {t(isSelected(option.value) ? "✓" : "")}
                        </span>
                        <span>{t(option.label)}</span>
                      </button>)}
                  </div>) : filteredActualOptions.map(option => <button key={option.value} type="button" className={`bb-tag-multiselect__option ${isSelected(option.value) ? "active" : ""}`} onClick={() => handleToggleOption(option.value)}>

                    <span className="bb-tag-multiselect__checkbox">
                      {t(isSelected(option.value) ? "✓" : "")}
                    </span>
                    <span>{t(option.label)}</span>
                  </button>) : <div className="bb-tag-multiselect__empty">{t("No relevant ")}
            {t(safeLabelText.toLowerCase())}{t(" found")}
          </div>}
          </div>}
      </div>

      {helperText ? <div className="bb-profile-form__helper-text">{t(helperText)}</div> : null}

      {showError ? <div className="invalid-feedback d-block">{t(errors[name])}</div> : null}
    </Form.Group>;
}
function SearchableSingleSelectField({
  label,
  name,
  options,
  value,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  placeholder,
  disabled = false,
  loading = false,
  emptyMessage,
  onSelectionChange,
  onTypingChange,
  showError
}) {
  const {
    language
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasFocused, setHasFocused] = useState(false);
  const wrapperRef = useRef(null);
  const safeLabelText = useMemo(() => getSafeLabelText(label, "options"), [label]);
  const normalizedOptions = useMemo(() => Array.isArray(options) ? options.map(normalizeOption) : [], [options]);
  const selectedOption = useMemo(() => {
    return normalizedOptions.find(item => item.value === String(value)) || null;
  }, [normalizedOptions, value]);
  const [inputValue, setInputValue] = useState(selectedOption?.label || "");
  useEffect(() => {
    setInputValue(selectedOption?.label || "");
  }, [selectedOption]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
        }
        if (hasInteracted || hasFocused) {
          setFieldTouched(name, true, true);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hasFocused, hasInteracted, isOpen, name, setFieldTouched]);
  const filteredOptions = useMemo(() => {
    const keyword = inputValue.trim().toLowerCase();
    if (!keyword) return normalizedOptions;
    return normalizedOptions.filter(item => matchesOption(item, keyword, language));
  }, [inputValue, normalizedOptions, language]);
  const handleInputChange = e => {
    if (disabled) return;
    const nextValue = e.target.value;
    setInputValue(nextValue);
    setFieldValue(name, "");
    setIsOpen(true);
    setHasInteracted(true);
    if (onTypingChange) {
      onTypingChange(nextValue);
    }
  };
  const handleSelectOption = option => {
    if (disabled) return;
    setInputValue(option.label);
    setFieldValue(name, option.value);
    setFieldTouched(name, true, true);
    setIsOpen(false);
    setHasInteracted(true);
    if (onSelectionChange) {
      onSelectionChange(option);
    }
  };
  return <Form.Group>
      <Form.Label>{t(label)}</Form.Label>

      <div ref={wrapperRef} className={`bb-search-select ${showError ? "is-invalid" : ""} ${disabled ? "disabled" : ""}`}>

        <Form.Control type="text" name={name} value={displayOptionInput(inputValue, normalizedOptions)} placeholder={t(loading ? `Loading ${safeLabelText}...` : placeholder)} autoComplete="off" onFocus={() => {
        if (disabled) return;
        setIsOpen(true);
        setHasFocused(true);
      }} onChange={handleInputChange} onBlur={() => {
        if (hasInteracted || hasFocused) {
          setFieldTouched(name, true, true);
        }
      }} isInvalid={showError} disabled={disabled} />


        {isOpen && !disabled && <div className="bb-search-select__menu">
            {filteredOptions.length > 0 ? filteredOptions.map(option => <button key={option.value} type="button" className={`bb-search-select__option ${String(value) === option.value ? "active" : ""}`} onMouseDown={e => e.preventDefault()} onClick={() => handleSelectOption(option)}>

                  {t(option.label)}
                </button>) : <div className="bb-search-select__empty">
                {t(emptyMessage || `No relevant ${safeLabelText.toLowerCase()} found`)}
              </div>}
          </div>}
      </div>

      {showError ? <div className="invalid-feedback d-block">{t(errors[name])}</div> : null}
    </Form.Group>;
}
export default function ProfileForm({
  initialValues,
  submitLabel,
  onSubmit,
  onCancel,
  lockIdentityFields = false
}) {
  useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(true);
  const [citiesError, setCitiesError] = useState("");
  const [nationalityOptions, setNationalityOptions] = useState([]);
  const [isNationalitiesLoading, setIsNationalitiesLoading] = useState(true);
  const [nationalitiesError, setNationalitiesError] = useState("");
  const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
  const [isPropertyTypesLoading, setIsPropertyTypesLoading] = useState(true);
  const [propertyTypesError, setPropertyTypesError] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedPreferredCities, setSelectedPreferredCities] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [isDistrictLoading, setIsDistrictLoading] = useState(false);
  const [preferredNeighbourhoodOptions, setPreferredNeighbourhoodOptions] = useState([]);
  const [preferredNeighbourhoodLabelMap, setPreferredNeighbourhoodLabelMap] = useState(() => buildInitialLabelMapFromItems(initialValues?.preferredNeighbourhood || []));
  const [preferredNeighbourhoodGroupedSections, setPreferredNeighbourhoodGroupedSections] = useState([]);
  const [preferredNeighbourhoodCache, setPreferredNeighbourhoodCache] = useState({});
  const [isPreferredNeighbourhoodLoading, setIsPreferredNeighbourhoodLoading] = useState(false);
  const [stepOneAttempted, setStepOneAttempted] = useState(false);
  const [stepTwoAttempted, setStepTwoAttempted] = useState(false);
  const [stepTwoInteractedFields, setStepTwoInteractedFields] = useState({});
  const markStepTwoInteracted = fieldName => {
    setStepTwoInteractedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };
  const normalizedInitialValues = useMemo(() => ({
    ...profileInitialValues,
    ...initialValues,
    gender: normalizeGenderValueForForm(initialValues?.gender),
    genderPreference: normalizeGenderPreferenceValueForForm(initialValues?.genderPreference),
    nationality: initialValues?.nationality !== undefined && initialValues?.nationality !== null ? String(initialValues.nationality) : "",
    city: initialValues?.city !== undefined && initialValues?.city !== null ? String(initialValues.city) : "",
    district: initialValues?.district !== undefined && initialValues?.district !== null ? String(initialValues.district) : "",
    roomTypePreference: normalizeRoomTypeMultiValueForForm(initialValues?.roomTypePreference),
    houseTypePreference: normalizeHouseTypeMultiValueForForm(initialValues?.houseTypePreference),
    preferredCity: normalizeIncomingMultiValue(initialValues?.preferredCity),
    preferredNeighbourhood: normalizeIncomingMultiValue(initialValues?.preferredNeighbourhood),
    budgetMin: initialValues?.budgetMin !== undefined && initialValues?.budgetMin !== null ? String(initialValues.budgetMin) : "100",
    budgetMax: initialValues?.budgetMax !== undefined && initialValues?.budgetMax !== null ? String(initialValues.budgetMax) : "50000"
  }), [initialValues]);
  const preferredCityInitialSignature = useMemo(() => getArraySignature(normalizedInitialValues.preferredCity), [normalizedInitialValues.preferredCity]);
  useEffect(() => {
    setSelectedCity(normalizedInitialValues.city || "");
  }, [normalizedInitialValues.city]);
  useEffect(() => {
    setSelectedPreferredCities(normalizeIncomingMultiValue(normalizedInitialValues.preferredCity));
  }, [preferredCityInitialSignature, normalizedInitialValues.preferredCity]);
  useEffect(() => {
    setPreferredNeighbourhoodLabelMap(prev => ({
      ...buildInitialLabelMapFromItems(initialValues?.preferredNeighbourhood || []),
      ...prev
    }));
  }, [initialValues?.preferredNeighbourhood]);
  useEffect(() => {
    let isMounted = true;
    const loadNationalities = async () => {
      try {
        setIsNationalitiesLoading(true);
        setNationalitiesError("");
        const response = await getNationalities();
        const isSuccess = response?.is_active === true || response?.status === true;
        const nationalitiesArray = Array.isArray(response?.data) ? response.data : [];
        const mappedNationalities = nationalitiesArray.filter(item => item?.id && item?.name).map(item => ({
          value: String(item.id),
          label: item.name,
          name_ar: item.name_ar || "",
          code: item.code || ""
        }));
        if (isMounted) {
          if (isSuccess) {
            setNationalityOptions(mappedNationalities);
            setNationalitiesError(mappedNationalities.length === 0 ? "Nationality options will appear after backend records are available." : "");
          } else {
            setNationalityOptions([]);
            setNationalitiesError("Unable to load nationalities.");
          }
        }
      } catch (error) {
        if (isMounted) {
          setNationalityOptions([]);
          setNationalitiesError(error?.response?.data?.message || error?.message || "Failed to load nationalities.");
        }
      } finally {
        if (isMounted) {
          setIsNationalitiesLoading(false);
        }
      }
    };
    loadNationalities();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadPropertyTypes = async () => {
      try {
        setIsPropertyTypesLoading(true);
        setPropertyTypesError("");
        const response = await getPropertyTypes();
        const isSuccess = response?.is_active === true || response?.status === true;
        const propertyTypesArray = Array.isArray(response?.data) ? response.data : [];
        const mappedPropertyTypes = propertyTypesArray.filter(item => item?.name).map(item => {
          const normalizedValue = normalizeHouseTypeValueForForm(item);
          if (!normalizedValue) return null;
          return {
            value: normalizedValue,
            label: HOUSE_TYPE_VALUE_LABEL_MAP[normalizedValue] || item.name || normalizedValue,
            id: item.id,
            name: item.name,
            name_ar: item.name_ar || ""
          };
        }).filter(Boolean);
        if (isMounted) {
          if (isSuccess) {
            setPropertyTypeOptions(mappedPropertyTypes);
            setPropertyTypesError("");
          } else {
            setPropertyTypeOptions([]);
            setPropertyTypesError("Unable to load property types.");
          }
        }
      } catch (error) {
        if (isMounted) {
          setPropertyTypeOptions([]);
          setPropertyTypesError(error?.response?.data?.message || error?.message || "Failed to load property types.");
        }
      } finally {
        if (isMounted) {
          setIsPropertyTypesLoading(false);
        }
      }
    };
    loadPropertyTypes();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadCities = async () => {
      try {
        setIsCitiesLoading(true);
        setCitiesError("");
        const response = await getCities();
        const isSuccess = response?.is_active === true || response?.status === true;
        const citiesArray = Array.isArray(response?.data) ? response.data : [];
        const mappedCities = citiesArray.filter(city => city?.id && city?.name).map(city => ({
          value: String(city.id),
          label: city.name
        }));
        if (isMounted) {
          if (isSuccess) {
            setCities(citiesArray);
            setCityOptions(mappedCities);
            setCitiesError("");
          } else {
            setCities([]);
            setCityOptions([]);
            setCitiesError("Unable to load cities.");
          }
        }
      } catch (error) {
        if (isMounted) {
          setCities([]);
          setCityOptions([]);
          setCitiesError(error?.response?.data?.message || error?.message || "Failed to load cities.");
        }
      } finally {
        if (isMounted) {
          setIsCitiesLoading(false);
        }
      }
    };
    loadCities();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadDistricts = async () => {
      if (!selectedCity) {
        setDistrictOptions([]);
        return;
      }
      try {
        setIsDistrictLoading(true);
        const response = await getNeighbourhoods(selectedCity);
        const isSuccess = response?.is_active === true || response?.status === true;
        const neighbourhoodsArray = Array.isArray(response?.data) ? response.data : [];
        if (isMounted) {
          if (isSuccess) {
            const mapped = neighbourhoodsArray.filter(item => item?.id && item?.name && String(item?.city_id) === String(selectedCity)).map(item => ({
              value: String(item.id),
              label: item.name,
              id: item.id,
              name: item.name,
              name_ar: item.name_ar || "",
              city_id: item.city_id
            }));
            setDistrictOptions(mapped);
          } else {
            setDistrictOptions([]);
          }
        }
      } catch (error) {
        if (isMounted) {
          setDistrictOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsDistrictLoading(false);
        }
      }
    };
    loadDistricts();
    return () => {
      isMounted = false;
    };
  }, [selectedCity]);
  useEffect(() => {
    let isMounted = true;
    const syncPreferredNeighbourhoodState = (cacheMap, selectedCityIds) => {
      const {
        groupedSections,
        flatOptions,
        labelMap
      } = buildPreferredNeighbourhoodMeta(selectedCityIds, cacheMap, cityOptions);
      if (!isMounted) return;
      setPreferredNeighbourhoodGroupedSections(groupedSections);
      setPreferredNeighbourhoodOptions(flatOptions);
      setPreferredNeighbourhoodLabelMap(prev => ({
        ...prev,
        ...labelMap
      }));
    };
    const loadPreferredNeighbourhoods = async () => {
      const normalizedSelectedCities = Array.isArray(selectedPreferredCities) ? Array.from(new Set(selectedPreferredCities.map(item => String(item)))) : [];
      if (normalizedSelectedCities.length === 0) {
        setPreferredNeighbourhoodGroupedSections([]);
        setPreferredNeighbourhoodOptions([]);
        setIsPreferredNeighbourhoodLoading(false);
        return;
      }
      const missingCityIds = normalizedSelectedCities.filter(cityId => !preferredNeighbourhoodCache[cityId]);
      if (missingCityIds.length === 0) {
        syncPreferredNeighbourhoodState(preferredNeighbourhoodCache, normalizedSelectedCities);
        return;
      }
      try {
        setIsPreferredNeighbourhoodLoading(true);
        const responses = await Promise.all(missingCityIds.map(async cityId => {
          const response = await getNeighbourhoods(cityId);
          return {
            cityId,
            response
          };
        }));
        const nextCache = {
          ...preferredNeighbourhoodCache
        };
        responses.forEach(({
          cityId,
          response
        }) => {
          const isSuccess = response?.is_active === true || response?.status === true;
          const neighbourhoodsArray = Array.isArray(response?.data) ? response.data : [];
          nextCache[cityId] = isSuccess ? neighbourhoodsArray.filter(item => item?.id && item?.name && String(item?.city_id) === String(cityId)) : [];
        });
        if (isMounted) {
          setPreferredNeighbourhoodCache(nextCache);
          syncPreferredNeighbourhoodState(nextCache, normalizedSelectedCities);
        }
      } catch (error) {
        if (isMounted) {
          syncPreferredNeighbourhoodState(preferredNeighbourhoodCache, normalizedSelectedCities);
        }
      } finally {
        if (isMounted) {
          setIsPreferredNeighbourhoodLoading(false);
        }
      }
    };
    loadPreferredNeighbourhoods();
    return () => {
      isMounted = false;
    };
  }, [selectedPreferredCities, preferredNeighbourhoodCache, cityOptions]);
  const nationalityOptionValues = useMemo(() => nationalityOptions.map(item => item.value), [nationalityOptions]);
  const cityOptionValues = useMemo(() => cityOptions.map(item => item.value), [cityOptions]);
  const districtOptionValues = useMemo(() => districtOptions.map(item => item.value), [districtOptions]);
  const propertyTypeOptionValues = useMemo(() => propertyTypeOptions.map(item => item.value), [propertyTypeOptions]);
  const roomTypeOptionValues = useMemo(() => roomTypeOptions.map(item => item.value), []);
  const genderPreferenceOptionValues = useMemo(() => genderPreferenceOptions.map(item => item.value), []);
  const preferredNeighbourhoodOptionValues = useMemo(() => preferredNeighbourhoodOptions.map(item => item.value), [preferredNeighbourhoodOptions]);
  const shouldStrictlyValidatePreferredNeighbourhood = useMemo(() => {
    return Array.isArray(selectedPreferredCities) && selectedPreferredCities.length > 0 && !isPreferredNeighbourhoodLoading && preferredNeighbourhoodOptionValues.length > 0;
  }, [selectedPreferredCities, isPreferredNeighbourhoodLoading, preferredNeighbourhoodOptionValues]);
  const validationShape = useMemo(() => ({
    name: Yup.string().trim().required("Full Name is required").max(30, "Full Name must not exceed 30 characters").matches(TEXT_ONLY_REGEX, "Full Name can contain only letters and spaces"),
    email: Yup.string().email("Enter a valid email address").required("Email Address is required"),
    contactNumber: Yup.string().matches(/^05\d{8}$/, "Contact Number must start with 05 and be 10 digits").required("Contact Number is required"),
    whatsappNumber: Yup.string().matches(/^05\d{8}$/, "WhatsApp Number must start with 05 and be 10 digits").required("WhatsApp Number is required"),
    age: Yup.number().typeError("Age must be a number").min(18, "Age must be at least 18").max(80, "Age cannot exceed 80").required("Age is required"),
    gender: Yup.string().required("Gender is required"),
    occupation: Yup.string().trim().required("Occupation is required").max(20, "Occupation must not exceed 20 characters").matches(TEXT_ONLY_REGEX, "Occupation can contain only letters and spaces"),
    nationality: Yup.string().required("Nationality is required").oneOf(nationalityOptionValues, "Please select a valid nationality"),
    city: Yup.string().required("City is required").oneOf(cityOptionValues, "Please select a valid city"),
    district: Yup.string().required("District/Neighbourhood is required").oneOf(districtOptionValues, "Please select a valid District/Neighbourhood"),
    preferredCity: Yup.array().of(Yup.string().oneOf(cityOptionValues, "Please select valid Preferred City options")).min(1, "At least one Preferred City is required").required("Preferred City is required"),
    preferredNeighbourhood: Yup.array().of(Yup.string()).min(1, "At least one Preferred Neighbourhood is required").required("Preferred Neighbourhood is required").test("preferred-neighbourhood-valid", "Please select valid Preferred Neighbourhood options", function (value) {
      if (!Array.isArray(value) || value.length === 0) {
        return true;
      }
      if (!shouldStrictlyValidatePreferredNeighbourhood) {
        return true;
      }
      const allowed = new Set(preferredNeighbourhoodOptionValues.map(item => String(item)));
      return value.every(item => allowed.has(String(item)));
    }),
    budgetMin: Yup.number().typeError("Minimum budget must be a number").required("Minimum budget is required").min(100, "Minimum budget must be at least SAR 100").max(50000, "Minimum budget cannot exceed SAR 50,000"),
    budgetMax: Yup.number().typeError("Maximum budget must be a number").required("Maximum budget is required").min(100, "Maximum budget must be at least SAR 100").max(50000, "Maximum budget cannot exceed SAR 50,000").test("budget-max-greater", "Maximum budget must be greater than or equal to minimum budget", function (value) {
      const {
        budgetMin
      } = this.parent;
      if (value === undefined || value === null || budgetMin === undefined || budgetMin === null) {
        return true;
      }
      return Number(value) >= Number(budgetMin);
    }),
    furnishedPreference: Yup.string().required("Furnished Preference is required"),
    roomTypePreference: Yup.array().of(Yup.string().oneOf(roomTypeOptionValues, "Please select valid Room Type Preference options")).min(1, "At least one Room Type Preference is required").required("Room Type Preference is required"),
    houseTypePreference: Yup.array().of(Yup.string().oneOf(propertyTypeOptionValues, "Please select valid House Type Preference options")).min(1, "At least one House Type Preference is required").required("House Type Preference is required"),
    genderPreference: Yup.string().required("Gender Preference is required").oneOf(genderPreferenceOptionValues, "Please select a valid Gender Preference"),
    aboutMe: Yup.string().nullable()
  }), [nationalityOptionValues, cityOptionValues, districtOptionValues, preferredNeighbourhoodOptionValues, shouldStrictlyValidatePreferredNeighbourhood, propertyTypeOptionValues, roomTypeOptionValues, genderPreferenceOptionValues]);
  const stepOneValidationSchema = useMemo(() => {
    const stepOneShape = stepOneFields.reduce((acc, field) => {
      acc[field] = validationShape[field];
      return acc;
    }, {});
    return Yup.object(stepOneShape);
  }, [validationShape]);
  const fullValidationSchema = useMemo(() => Yup.object(validationShape), [validationShape]);
  return <Formik initialValues={normalizedInitialValues} validationSchema={currentStep === 1 ? stepOneValidationSchema : fullValidationSchema} enableReinitialize validateOnMount={false} onSubmit={(values, formikHelpers) => {
    const payload = {
      profile: {
        age: values.age ? Number(values.age) : "",
        gender: normalizeGenderValueForApi(values.gender),
        nationality_id: values.nationality ? Number(values.nationality) : "",
        occupation: values.occupation?.trim() || "",
        about_me: values.aboutMe?.trim() || "",
        whatsapp_number: values.whatsappNumber || "",
        city_id: values.city ? Number(values.city) : "",
        neighbourhood_id: values.district ? Number(values.district) : ""
      },
      preferences: {
        preferred_city_id: Array.isArray(values.preferredCity) ? values.preferredCity.map(item => Number(item)) : [],
        preferred_neighbourhood_id: Array.isArray(values.preferredNeighbourhood) ? values.preferredNeighbourhood.map(item => Number(item)) : [],
        budget_min: values.budgetMin ? Number(values.budgetMin) : 0,
        budget_max: values.budgetMax ? Number(values.budgetMax) : 0,
        furnished_preference: normalizeYesNoValueForApi(values.furnishedPreference),
        room_type_preference: normalizeRoomTypeValuesForApi(values.roomTypePreference),
        house_type_preference: normalizeHouseTypeValuesForApi(values.houseTypePreference),
        gender_preference: normalizeGenderValueForApi(values.genderPreference)
      }
    };
    onSubmit(payload, formikHelpers);
  }}>

      {({
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      handleSubmit,
      setFieldValue,
      setFieldTouched,
      validateForm,
      setTouched
    }) => {
      const getFieldErrorState = fieldName => {
        const hasError = !!errors[fieldName];
        if (!hasError) return false;
        if (stepOneFields.includes(fieldName)) {
          return !!touched[fieldName] || stepOneAttempted;
        }
        return !!stepTwoInteractedFields[fieldName] || stepTwoAttempted;
      };
      const handleNextStep = async () => {
        setStepOneAttempted(true);
        const validationErrors = await validateForm();
        const hasStepErrors = stepOneFields.some(field => !!validationErrors[field]);
        const nextTouchedState = {
          ...buildUntouchedState(stepOneFields),
          ...buildUntouchedState(stepTwoFields)
        };
        stepOneFields.forEach(field => {
          nextTouchedState[field] = true;
        });
        stepTwoFields.forEach(field => {
          nextTouchedState[field] = false;
        });
        setTouched(nextTouchedState, false);
        if (!hasStepErrors) {
          setCurrentStep(2);
          setStepTwoAttempted(false);
          setStepTwoInteractedFields({});
        }
      };
      const handlePreviousStep = () => {
        setCurrentStep(1);
        setStepTwoAttempted(false);
        setStepTwoInteractedFields({});
        const resetTouchedState = {
          ...buildUntouchedState(stepOneFields),
          ...buildUntouchedState(stepTwoFields)
        };
        stepOneFields.forEach(field => {
          resetTouchedState[field] = touched[field] || false;
        });
        stepTwoFields.forEach(field => {
          resetTouchedState[field] = false;
        });
        setTouched(resetTouchedState, false);
      };
      const handleFinalSubmit = async () => {
        setStepTwoAttempted(true);
        const validationErrors = await validateForm();
        const hasErrors = [...stepOneFields, ...stepTwoFields].some(field => !!validationErrors[field]);
        if (hasErrors) return;
        handleSubmit();
      };
      return <Form onSubmit={e => e.preventDefault()}>
            <div className="bb-profile-stepper">
              <div className="bb-profile-stepper__items">
                <div className={`bb-profile-stepper__item ${currentStep >= 1 ? "active" : ""}`}>

                  <div className="bb-profile-stepper__circle">1</div>
                  <div className="bb-profile-stepper__label">{t("Personal Information")}

                  </div>
                </div>

                <div className={`bb-profile-stepper__line ${currentStep >= 2 ? "active" : ""}`} />


                <div className={`bb-profile-stepper__item ${currentStep >= 2 ? "active" : ""}`}>

                  <div className="bb-profile-stepper__circle">2</div>
                  <div className="bb-profile-stepper__label">{t("Living Preferences")}

                  </div>
                </div>
              </div>
            </div>

            {nationalitiesError ? <div className="mb-3 text-danger">{t(nationalitiesError)}</div> : null}

            {citiesError ? <div className="mb-3 text-danger">{t(citiesError)}</div> : null}

            {propertyTypesError ? <div className="mb-3 text-danger">{t(propertyTypesError)}</div> : null}

            {currentStep === 1 && <div className="bb-profile-form-section">
                <h3 className="bb-profile-form-section__title">{t("Personal Information")}

              </h3>

                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Full Name")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="text" name="name" value={values.name} onChange={e => setFieldValue("name", sanitizeTextOnly(e.target.value, 30))} onBlur={handleBlur} isInvalid={getFieldErrorState("name")} maxLength={30} placeholder={t("Enter full name")} disabled={lockIdentityFields} readOnly={lockIdentityFields} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.name)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Email Address")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="email" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} isInvalid={getFieldErrorState("email")} placeholder={t("Enter email address")} disabled={lockIdentityFields} readOnly={lockIdentityFields} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.email)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Contact Number")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="text" name="contactNumber" value={values.contactNumber} onChange={e => setFieldValue("contactNumber", sanitizePhoneNumber(e.target.value))} onBlur={handleBlur} isInvalid={getFieldErrorState("contactNumber")} placeholder={t("05XXXXXXXX")} inputMode="numeric" maxLength={10} disabled={lockIdentityFields} readOnly={lockIdentityFields} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.contactNumber)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("WhatsApp Number")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="text" name="whatsappNumber" value={values.whatsappNumber} onChange={e => setFieldValue("whatsappNumber", sanitizePhoneNumber(e.target.value))} onBlur={handleBlur} isInvalid={getFieldErrorState("whatsappNumber")} placeholder={t("05XXXXXXXX")} inputMode="numeric" maxLength={10} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.whatsappNumber)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("Age")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="number" name="age" value={values.age} onChange={handleChange} onBlur={handleBlur} isInvalid={getFieldErrorState("age")} placeholder={t("Enter age")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.age)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("Gender")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="gender" value={values.gender} onChange={handleChange} onBlur={handleBlur} isInvalid={getFieldErrorState("gender")}>

                        <option value="">{t("Select Gender")}</option>
                        {genderOptions.map(item => <option key={item} value={item}>
                            {t(item)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.gender)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("Occupation")}
                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="text" name="occupation" value={values.occupation} onChange={e => setFieldValue("occupation", sanitizeTextOnly(e.target.value, 20))} onBlur={handleBlur} isInvalid={getFieldErrorState("occupation")} maxLength={20} placeholder={t("Enter occupation")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.occupation)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <SearchableSingleSelectField label={<>{t("Nationality ")}<span className="bb-required-mark">*</span>
                        </>} name="nationality" options={nationalityOptions} value={values.nationality} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} placeholder={t("Search nationality")} emptyMessage={t("No relevant nationality found")} disabled={isNationalitiesLoading} loading={isNationalitiesLoading} showError={getFieldErrorState("nationality")} />

                  </Col>

                  <Col md={6}>
                    <SearchableSingleSelectField label={<>{t("City ")}<span className="bb-required-mark">*</span>
                        </>} name="city" options={cityOptions} value={values.city} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} placeholder={t("Search city")} emptyMessage={t("No relevant city found")} disabled={isCitiesLoading} loading={isCitiesLoading} showError={getFieldErrorState("city")} onTypingChange={() => {
                setSelectedCity("");
                setFieldValue("district", "");
                setDistrictOptions([]);
              }} onSelectionChange={option => {
                const selectedCityValue = String(option.value);
                setFieldValue("city", selectedCityValue);
                setFieldValue("district", "");
                setSelectedCity(selectedCityValue);
                setDistrictOptions([]);
              }} />

                  </Col>

                  <Col md={6}>
                    <SearchableSingleSelectField label={<>{t("District/Neighbourhood")}<span className="bb-required-mark">*</span>
                        </>} name="district" options={districtOptions} value={values.district} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} placeholder={t(!values.city ? "Select City First" : "Search district/neighbourhood")} emptyMessage={t("No relevant district/neighbourhood found")} disabled={!values.city || isDistrictLoading} loading={isDistrictLoading} showError={getFieldErrorState("district")} />

                  </Col>
                </Row>
              </div>}

            {currentStep === 2 && <div className="bb-profile-form-section">
                <h3 className="bb-profile-form-section__title">{t("Living Preferences")}

              </h3>

                <Row className="g-4">
                  <Col md={6}>
                    <MultiSelectTagField label={<>{t("Preferred City")}<span className="bb-required-mark">*</span>
                        </>} name="preferredCity" options={cityOptions} values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} helperText={t("Select at least one preferred city.")} disabled={isCitiesLoading} loading={isCitiesLoading} searchable searchPlaceholder={t("Search preferred city")} showError={getFieldErrorState("preferredCity")} onInteract={markStepTwoInteracted} onSelectionChange={updatedCities => {
                const previousSignature = getArraySignature(values.preferredCity);
                const nextSignature = getArraySignature(updatedCities);
                setSelectedPreferredCities(updatedCities);
                if (previousSignature !== nextSignature) {
                  setFieldValue("preferredNeighbourhood", []);
                }
                if (!updatedCities || updatedCities.length === 0) {
                  setPreferredNeighbourhoodGroupedSections([]);
                  setPreferredNeighbourhoodOptions([]);
                }
              }} />

                  </Col>

                  <Col md={6}>
                    <MultiSelectTagField label={<>{t("Preferred Neighbourhood")}<span className="bb-required-mark">*</span>
                        </>} name="preferredNeighbourhood" options={preferredNeighbourhoodOptions} groupedSections={preferredNeighbourhoodGroupedSections} values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} helperText={t("Select at least one preferred neighbourhood.")} disabled={!Array.isArray(values.preferredCity) || values.preferredCity.length === 0 || isPreferredNeighbourhoodLoading} loading={isPreferredNeighbourhoodLoading} searchable searchPlaceholder={t("Search preferred neighbourhood")} showError={getFieldErrorState("preferredNeighbourhood")} onInteract={markStepTwoInteracted} optionLabelMap={preferredNeighbourhoodLabelMap} />

                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Budget per Month From (SAR)")}

                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="number" name="budgetMin" value={values.budgetMin} onChange={e => {
                  markStepTwoInteracted("budgetMin");
                  handleChange(e);
                }} onBlur={e => {
                  markStepTwoInteracted("budgetMin");
                  handleBlur(e);
                }} min={100} max={50000} step={100} isInvalid={getFieldErrorState("budgetMin")} placeholder={t("Minimum budget")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.budgetMin)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Budget per Month To (SAR)")}

                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Control type="number" name="budgetMax" value={values.budgetMax} onChange={e => {
                  markStepTwoInteracted("budgetMax");
                  handleChange(e);
                }} onBlur={e => {
                  markStepTwoInteracted("budgetMax");
                  handleBlur(e);
                }} min={100} max={50000} step={100} isInvalid={getFieldErrorState("budgetMax")} placeholder={t("Maximum budget")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.budgetMax)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Furnished Preference")}

                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="furnishedPreference" value={values.furnishedPreference} onChange={e => {
                  markStepTwoInteracted("furnishedPreference");
                  handleChange(e);
                }} onBlur={e => {
                  markStepTwoInteracted("furnishedPreference");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("furnishedPreference")}>

                        <option value="">{t("Select Furnished Preference")}</option>
                        {furnishedOptions.map(item => <option key={item} value={item}>
                            {t(item)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.furnishedPreference)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <MultiSelectTagField label={<>{t("Room Type Preference")}<span className="bb-required-mark">*</span>
                        </>} name="roomTypePreference" options={roomTypeOptions} values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} helperText={t("You can select multiple room type preferences.")} showError={getFieldErrorState("roomTypePreference")} onInteract={markStepTwoInteracted} />

                  </Col>

                  <Col md={6}>
                    <MultiSelectTagField label={<>{t("House Type Preference")}<span className="bb-required-mark">*</span>
                        </>} name="houseTypePreference" options={propertyTypeOptions} values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} helperText={t("You can select multiple house type preferences.")} showError={getFieldErrorState("houseTypePreference")} onInteract={markStepTwoInteracted} disabled={isPropertyTypesLoading} loading={isPropertyTypesLoading} searchable searchPlaceholder={t("Search house type")} />

                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("Gender Preference")}

                      <span className="bb-required-mark">*</span>
                      </Form.Label>
                      <Form.Select name="genderPreference" value={values.genderPreference} onChange={e => {
                  markStepTwoInteracted("genderPreference");
                  handleChange(e);
                }} onBlur={e => {
                  markStepTwoInteracted("genderPreference");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("genderPreference")}>

                        <option value="">{t("Select Gender Preference")}</option>
                        {genderPreferenceOptions.map(item => <option key={item.value} value={item.value}>
                            {t(item.label)}
                          </option>)}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {t(errors.genderPreference)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>{t("About Me")}</Form.Label>
                      <Form.Control as="textarea" rows={5} name="aboutMe" value={values.aboutMe} onChange={e => {
                  markStepTwoInteracted("aboutMe");
                  handleChange(e);
                }} onBlur={e => {
                  markStepTwoInteracted("aboutMe");
                  handleBlur(e);
                }} isInvalid={getFieldErrorState("aboutMe")} placeholder={t("Write something about yourself (optional)")} />

                      <Form.Control.Feedback type="invalid">
                        {t(errors.aboutMe)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>}

            <div className="bb-profile-form__actions">
              {currentStep === 1 ? <>
                  <Button variant="outline-secondary" type="button" onClick={onCancel}>{t("Cancel")}

                </Button>
                  <Button type="button" className="bb-profile-form-btn" onClick={handleNextStep} disabled={isCitiesLoading || isNationalitiesLoading || isPropertyTypesLoading}>

                    {isCitiesLoading || isNationalitiesLoading || isPropertyTypesLoading ? <>
                        <Spinner size="sm" animation="border" className="me-2" />{t("Loading...")}

                  </> : t("Next")}
                  </Button>
                </> : <>
                  <Button variant="outline-secondary" type="button" onClick={handlePreviousStep}>{t("Back")}


                </Button>
                  <Button type="button" className="bb-profile-form-btn" onClick={handleFinalSubmit} disabled={isCitiesLoading || isNationalitiesLoading || isPropertyTypesLoading}>

                    {t(submitLabel)}
                  </Button>
                </>}
            </div>
          </Form>;
    }}
    </Formik>;
}
